import { useEffect, useState } from "react";
import { api, photoUrl } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { PopupShell, track } from "./PopupShell";

export { PopupCard } from "./PopupShell";

const DAY = 24 * 3600 * 1000;
const VISIT = "cp_popup_visit"; // only one page's popup per visit
const keyFor = (page) => (page === "home" ? "cp_home_popup" : `cp_popup_${page}`);

const alreadySeen = (p, KEY) => {
  if (p.frequency === "every") return false;
  if (p.frequency === "session") return sessionStorage.getItem(KEY) === p.version;
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || "{}");
    if (s.version !== p.version) return false;
    return p.frequency === "once" || Date.now() - s.at < DAY;
  } catch { return false; }
};

const markSeen = (p, KEY) => {
  if (p.frequency === "session") sessionStorage.setItem(KEY, p.version);
  else localStorage.setItem(KEY, JSON.stringify({ version: p.version, at: Date.now() }));
};

// Each visitor sticks to one version for the whole test
const withVariant = (data, KEY) => {
  if (!data.ab) return data;
  let v;
  try { const s = JSON.parse(localStorage.getItem(`${KEY}_ab`) || "{}"); if (s.id === data.ab.id) v = s.v; } catch { /* new visitor */ }
  if (!v) {
    v = Math.random() * 100 < data.ab.split ? "B" : "A";
    localStorage.setItem(`${KEY}_ab`, JSON.stringify({ id: data.ab.id, v }));
  }
  return { ...data, ...(v === "B" ? data.ab.b : {}), _ab: data.ab.id, _v: v };
};

// Fills {breed} and {city} in listing popups
const TEXT_KEYS = ["title", "title_fr", "body", "body_fr", "button_text", "button_text_fr", "button2_text", "button2_text_fr", "signup_button_text", "signup_button_text_fr", "signup_thanks", "signup_thanks_fr"];
const fill = (data, ctx) => {
  if (!ctx) return data;
  const out = { ...data, _ctx: ctx };
  TEXT_KEYS.forEach((k) => { if (out[k]) out[k] = out[k].replaceAll("{breed}", ctx.breed).replaceAll("{city}", ctx.city || ""); });
  return out;
};

export function HomePopup({ page = "home", ctx = null }) {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const authKnown = user !== null;
  const signedIn = !!user;

  useEffect(() => {
    if (!authKnown) return undefined;
    let fired = false;
    let cancelled = false;
    const offs = [];
    const cleanup = () => { offs.splice(0).forEach((f) => f()); };
    const KEY = keyFor(page);
    const otherShown = () => { const v = sessionStorage.getItem(VISIT); return v && v !== page; };
    if (otherShown()) return undefined;
    api.get("/site/popup", { params: { page } }).then(({ data: raw }) => {
      if (!raw.enabled || alreadySeen(raw, KEY) || (raw.signed_out_only && signedIn)) return;
      const data = fill({ ...withVariant(raw, KEY), _page: page, _key: KEY }, ctx);
      setP(data);
      const img = data.image ? new Promise((r) => { const i = new Image(); i.onload = i.onerror = r; i.src = photoUrl(data.image); }) : null;
      const fire = () => {
        if (fired) return;
        fired = true;
        cleanup();
        Promise.resolve(img).then(() => {
          if (cancelled || otherShown()) return;
          sessionStorage.setItem(VISIT, page);
          setOpen(true);
          track(data, "view");
        });
      };
      // Phones can't signal "about to leave", so they fall back to the delay
      const touch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
      if (data.trigger_delay !== false || (data.trigger_exit && touch)) {
        const t = setTimeout(fire, (data.delay_seconds || 0) * 1000);
        offs.push(() => clearTimeout(t));
      }
      if (data.trigger_scroll) {
        const onScroll = () => {
          const room = document.documentElement.scrollHeight - window.innerHeight;
          if (room > 0 && (window.scrollY / room) * 100 >= (data.scroll_percent || 50)) fire();
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        offs.push(() => window.removeEventListener("scroll", onScroll));
      }
      if (data.trigger_exit && !touch) {
        const onLeave = (e) => { if (e.clientY <= 0) fire(); };
        document.documentElement.addEventListener("mouseleave", onLeave);
        offs.push(() => document.documentElement.removeEventListener("mouseleave", onLeave));
      }
    }).catch(() => {});
    return () => { cancelled = true; cleanup(); };
  }, [authKnown, signedIn, page, ctx?.id]);

  const close = () => { markSeen(p, p._key); setOpen(false); };
  if (!p) return null;
  return <PopupShell p={p} open={open} onClose={close} onSignup={() => markSeen(p, p._key)} lang={lang} page={page} />;
}
